import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { api, ApiError, type ReadTokenRow, type SourceRow, type SourceStatus } from '@/api/client';
import { Secret } from '@/components/Secret';
import { ago, compactCount, ENV_ORDER, envStyle, formatBytes, formatCount, shortStamp } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { useShell } from '@/lib/shell';

const rank = (env: string): number => {
    const index = ENV_ORDER.indexOf(env);

    return index < 0 ? ENV_ORDER.length : index;
};

interface ShownSecret {
    title: string;
    token: string;
    extra: string;
    note: ReactNode;
}

// A source's token, with what bustard needs.
const sourceSecret = (title: string, token: string): ShownSecret => ({
    title,
    token,
    extra: `LOGTAIL_MIRROR_ENDPOINT=${location.origin}/\nLOGTAIL_MIRROR_RESOURCE_KEY=${token}`,
    note: (
        <>
            Copy it now: the server keeps only its hash, and this is the last time it is shown. For bustard, the two lines go in the
            environment&apos;s group on Render (<b>.env.infra.&lt;env&gt;</b>), then the api, cron and ms-media deploy.
        </>
    ),
});

// Where the lines come from, and who else reads them: one token per source, bound to an environment, and read tokens for
// machines (FLBI, scripts). Everyone sees how each one is doing; admins create, rotate and revoke them. A token is shown
// once; the server keeps only its hash.
export function Sources(): ReactNode {
    const { user, say } = useShell();
    const queryClient = useQueryClient();
    const admin = user.role === 'admin';
    const sources = useQuery({ queryKey: ['sources'], queryFn: api.sources });
    const status = useQuery({ queryKey: ['status'], queryFn: api.status, refetchInterval: 10_000 });
    const [secret, setSecret] = useState<ShownSecret | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirming, setConfirming] = useState<string | null>(null);
    const refresh = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['sources'] });
        await queryClient.invalidateQueries({ queryKey: ['envs'] });
    };
    const rotate = useMutation({
        mutationFn: (source: SourceRow) => api.rotateSource(source.id),
        onSuccess: async ({ token }, source) => {
            setSecret(sourceSecret(`A new token for ${source.name}`, token));
            setConfirming(null);
            await refresh();
        },
        onError: (error) => say(error instanceof ApiError ? error.message : 'Not rotated'),
    });
    const revoke = useMutation({
        mutationFn: (source: SourceRow) => api.revokeSource(source.id),
        onSuccess: async (_, source) => {
            say(`${source.name} revoked: its token no longer ingests`);
            setConfirming(null);
            await refresh();
        },
    });
    const live = (sources.data?.sources ?? [])
        .filter((source) => !source.revoked_at)
        .sort((a, b) => rank(a.env) - rank(b.env) || a.name.localeCompare(b.name));
    const revoked = (sources.data?.sources ?? []).filter((source) => source.revoked_at);
    const byId = new Map((status.data?.sources ?? []).map((item) => [item.id, item]));
    const now = useNow(10_000);

    return (
        <div className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Sources</h1>
                    <p>
                        Each source is a token bound to one environment: bustard sends with it, the server files every line under that environment.
                        Lines a minute and rejections are counted since the server last started. Read tokens let a service, a script or Claude Code
                        read the logs.
                    </p>
                </div>
                {admin && (
                    <button className="act primary" onClick={() => setCreating(!creating)}>
                        {creating ? 'Close' : '+ New source'}
                    </button>
                )}
            </div>
            {secret && (
                <Secret
                    title={secret.title}
                    value={secret.token}
                    extra={secret.extra}
                    onCopy={say}
                    onClose={() => setSecret(null)}
                    note={secret.note}
                />
            )}
            {creating && (
                <NewSource
                    envs={[...new Set(live.map((source) => source.env))]}
                    onCreated={async (name, token) => {
                        setCreating(false);
                        setSecret(sourceSecret(`The token of ${name}`, token));
                        await refresh();
                    }}
                    say={say}
                />
            )}
            <div className="panel">
                <table className="ptable">
                    <thead>
                        <tr className="micro">
                            <th>Environment</th>
                            <th>Source</th>
                            <th className="num">Lines / min</th>
                            <th>Last line</th>
                            <th>Services</th>
                            <th className="num">Refused</th>
                            <th className="num">Kept</th>
                            <th>Token</th>
                            {admin && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {live.map((source) => (
                            <SourceRowView
                                key={source.id}
                                source={source}
                                status={byId.get(source.id)}
                                now={now}
                                admin={admin}
                                confirming={confirming}
                                onConfirm={setConfirming}
                                onRotate={() => rotate.mutate(source)}
                                onRevoke={() => revoke.mutate(source)}
                            />
                        ))}
                        {live.length === 0 && !sources.isLoading && (
                            <tr>
                                <td colSpan={9} className="ptable-empty">
                                    No source yet{admin ? ': create the first one above.' : '.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <ReadTokens
                admin={admin}
                envs={[...new Set(live.map((source) => source.env))].sort((a, b) => rank(a) - rank(b))}
                now={now}
                say={say}
                onSecret={setSecret}
            />
            <StoragePanel now={now} />
            {revoked.length > 0 && (
                <details className="revoked">
                    <summary className="micro">Revoked · {revoked.length}</summary>
                    <table className="ptable dim">
                        <tbody>
                            {revoked.map((source) => (
                                <tr key={source.id}>
                                    <td>
                                        <EnvBadge env={source.env} />
                                    </td>
                                    <td className="mono">{source.name}</td>
                                    <td className="mono">…{source.token_hint}</td>
                                    <td className="micro">Revoked {ago(Date.parse(source.revoked_at as string), now)} ago</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
        </div>
    );
}

export function EnvBadge({ env }: { env: string }): ReactNode {
    return (
        <span className="envbadge" style={{ ['--c' as string]: envStyle(env).color }}>
            <i />
            {env}
        </span>
    );
}

function SourceRowView(props: {
    source: SourceRow;
    status: SourceStatus | undefined;
    now: number;
    admin: boolean;
    confirming: string | null;
    onConfirm: (key: string | null) => void;
    onRotate: () => void;
    onRevoke: () => void;
}): ReactNode {
    const { source, status } = props;
    const lastAt = status?.lastAt ? Date.parse(status.lastAt) : source.last_seen_at ? Date.parse(source.last_seen_at) : null;
    const quiet = lastAt === null || props.now - lastAt > 5 * 60_000;
    const refused = Object.values(status?.rejected ?? {}).reduce((sum, count) => sum + count, 0);
    const key = (what: string): string => `${what}:${source.id}`;

    return (
        <tr>
            <td>
                <EnvBadge env={source.env} />
            </td>
            <td>
                <div className="mono strong">{source.name}</div>
                <div className="micro sub">{source.profile === 'bustard' ? 'bustard profile' : 'Generic'}</div>
            </td>
            <td className="num mono">{status ? formatCount(Math.round(status.linesPerMinute)) : '–'}</td>
            <td>
                <span className={`dot ${quiet ? 'quiet' : 'ok'}`} />
                {lastAt ? `${ago(lastAt, props.now)} ago` : 'never'}
            </td>
            <td className="mono small">
                {(status?.services ?? []).map((service) => `${service.service} ${Math.round(service.linesPerMinute)}`).join(' · ') || '–'}
            </td>
            <td className={`num mono ${refused > 0 ? 'warn' : ''}`} title={JSON.stringify(status?.rejected ?? {})}>
                {refused}
            </td>
            <td className="num mono">{source.retention_days} days</td>
            <td className="mono">fls_…{source.token_hint}</td>
            {props.admin && (
                <td className="row-actions">
                    <button
                        className={`act ${props.confirming === key('rotate') ? 'primary' : ''}`}
                        title="A new token; the old one stops at once"
                        onClick={() => (props.confirming === key('rotate') ? props.onRotate() : props.onConfirm(key('rotate')))}
                    >
                        {props.confirming === key('rotate') ? 'Rotate now?' : 'Rotate'}
                    </button>
                    <button
                        className={`act ${props.confirming === key('revoke') ? 'danger' : ''}`}
                        title="The token stops ingesting; the lines stay"
                        onClick={() => (props.confirming === key('revoke') ? props.onRevoke() : props.onConfirm(key('revoke')))}
                    >
                        {props.confirming === key('revoke') ? 'Revoke now?' : 'Revoke'}
                    </button>
                </td>
            )}
        </tr>
    );
}

function NewSource(props: { envs: string[]; onCreated: (name: string, token: string) => Promise<void>; say: (message: string) => void }): ReactNode {
    const [env, setEnv] = useState(props.envs[0] ?? 'staging');
    const [name, setName] = useState('');
    const [profile, setProfile] = useState<'bustard' | 'generic'>('bustard');
    const [retention, setRetention] = useState(7);
    const [error, setError] = useState<string | null>(null);
    const create = useMutation({
        mutationFn: () => api.createSource({ name: name.trim() || `${env}-bustard`, env, profile, retentionDays: retention }),
        onSuccess: ({ token }) => props.onCreated(name.trim() || `${env}-bustard`, token),
        onError: (reason) => setError(reason instanceof ApiError ? reason.message : 'Not created'),
    });

    return (
        <form
            className="panel form-card"
            onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                create.mutate();
            }}
        >
            <div className="panel-head">
                <span className="title">New source</span>
                <span className="note">its token is shown once</span>
            </div>
            <div className="form-grid">
                <label>
                    <span className="micro">Environment</span>
                    <input
                        list="known-envs"
                        value={env}
                        onChange={(event) => setEnv(event.target.value.toLowerCase())}
                        pattern="[a-z0-9_-]{1,32}"
                        required
                    />
                    <datalist id="known-envs">
                        {['prod', 'preprod', 'demo', 'staging', 'local'].map((item) => (
                            <option key={item} value={item} />
                        ))}
                    </datalist>
                </label>
                <label>
                    <span className="micro">Name</span>
                    <input value={name} placeholder={`${env}-bustard`} onChange={(event) => setName(event.target.value)} maxLength={64} />
                </label>
                <label>
                    <span className="micro">Profile</span>
                    <select value={profile} onChange={(event) => setProfile(event.target.value as 'bustard' | 'generic')}>
                        <option value="bustard">bustard (its request dumps, users, exceptions)</option>
                        <option value="generic">generic (any Logtail client)</option>
                    </select>
                </label>
                <label>
                    <span className="micro">Keep lines for</span>
                    <select value={retention} onChange={(event) => setRetention(Number(event.target.value))}>
                        {[3, 7, 14, 30, 60, 90].map((days) => (
                            <option key={days} value={days}>
                                {days} days
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="dialog-actions">
                <button className="act primary" disabled={create.isPending}>
                    {create.isPending ? 'Creating…' : 'Create the source'}
                </button>
            </div>
        </form>
    );
}

// Read tokens: what machines read with (the API under /api/v1 and the stream), each for some environments or every one.
function ReadTokens(props: {
    admin: boolean;
    envs: string[];
    now: number;
    say: (message: string) => void;
    onSecret: (secret: ShownSecret) => void;
}): ReactNode {
    const queryClient = useQueryClient();
    const tokens = useQuery({ queryKey: ['tokens'], queryFn: api.tokens });
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [every, setEvery] = useState(false);
    const [picked, setPicked] = useState<string[]>([]);
    const [confirming, setConfirming] = useState<string | null>(null);
    const fail = (error: unknown): void => props.say(error instanceof ApiError ? error.message : 'The server did not answer');
    const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['tokens'] });
    const create = useMutation({
        mutationFn: () => api.createToken({ name: name.trim(), envs: every ? '*' : picked }),
        onSuccess: async ({ token }) => {
            props.onSecret({
                title: `The read token ${name.trim()}`,
                token,
                extra: `FLEEQUID_LOGS_URL=${location.origin}\nFLEEQUID_LOGS_TOKEN=${token}\n\nclaude mcp add --transport http --scope user fleequid-logs ${location.origin}/api/v1/mcp --header "Authorization: Bearer ${token}"`,
                note: (
                    <>
                        Copy it now: the server keeps only its hash. A service sends it as <b>Authorization: Bearer</b> to <b>/api/v1</b> (the stream,
                        search, issues, the alerts feed), the two variables in its environment. For Claude Code, the last line in a terminal gives
                        every project the logs as tools (user scope: never in a repository).
                    </>
                ),
            });
            setAdding(false);
            setName('');
            setPicked([]);
            setEvery(false);
            await refresh();
        },
        onError: fail,
    });
    const revoke = useMutation({
        mutationFn: (token: ReadTokenRow) => api.revokeToken(token.id),
        onSuccess: async (_, token) => {
            props.say(`${token.name} revoked: it reads nothing from now on`);
            setConfirming(null);
            await refresh();
        },
        onError: fail,
    });
    const rows = (tokens.data?.tokens ?? []).filter((token) => !token.revoked_at);
    const revoked = (tokens.data?.tokens ?? []).length - rows.length;
    const choices = [...new Set([...props.envs, ...picked])];

    return (
        <div className="panel">
            <div className="panel-head">
                <span className="title">Read tokens</span>
                <span className="note">for services, scripts and Claude Code · /api/v1</span>
                <span className="spacer" />
                {revoked > 0 && <span className="micro dim-note">{revoked} revoked</span>}
                {props.admin && (
                    <button className="act" onClick={() => setAdding(!adding)}>
                        {adding ? 'Close' : '+ Read token'}
                    </button>
                )}
            </div>
            {adding && (
                <form
                    className="token-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (name.trim() && (every || picked.length > 0)) {
                            create.mutate();
                        }
                    }}
                >
                    <input
                        placeholder="Claude Code · your name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        maxLength={64}
                        required
                        autoFocus
                    />
                    <span className="micro">Reads</span>
                    <label className="check">
                        <input type="checkbox" checked={every} onChange={(event) => setEvery(event.target.checked)} />
                        every environment
                    </label>
                    {!every &&
                        choices.map((env) => (
                            <label key={env} className="check">
                                <input
                                    type="checkbox"
                                    checked={picked.includes(env)}
                                    onChange={(event) => setPicked(event.target.checked ? [...picked, env] : picked.filter((item) => item !== env))}
                                />
                                <EnvBadge env={env} />
                            </label>
                        ))}
                    <button className="act primary" disabled={!name.trim() || (!every && picked.length === 0) || create.isPending}>
                        {create.isPending ? 'Creating…' : 'Create'}
                    </button>
                </form>
            )}
            <table className="ptable compact">
                <thead>
                    <tr className="micro">
                        <th>Name</th>
                        <th>Reads</th>
                        <th>Token</th>
                        <th>Last used</th>
                        <th>Created</th>
                        {props.admin && <th />}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((token) => (
                        <tr key={token.id}>
                            <td className="strong">{token.name}</td>
                            <td>
                                {token.envs === '*' ? (
                                    <span className="micro">Every environment</span>
                                ) : (
                                    <span className="env-list">
                                        {token.envs.map((env) => (
                                            <EnvBadge key={env} env={env} />
                                        ))}
                                    </span>
                                )}
                            </td>
                            <td className="mono">flr_…{token.token_hint}</td>
                            <td>
                                <span
                                    className={`dot ${token.last_used_at && props.now - Date.parse(token.last_used_at) < 5 * 60_000 ? 'ok' : 'quiet'}`}
                                />
                                {token.last_used_at ? `${ago(Date.parse(token.last_used_at), props.now)} ago` : 'never'}
                            </td>
                            <td className="small">
                                {ago(Date.parse(token.created_at), props.now)} ago{token.author ? ` · ${token.author}` : ''}
                            </td>
                            {props.admin && (
                                <td className="row-actions">
                                    <button
                                        className={`act ${confirming === token.id ? 'danger' : ''}`}
                                        title="It reads nothing from now on"
                                        onClick={() => (confirming === token.id ? revoke.mutate(token) : setConfirming(token.id))}
                                    >
                                        {confirming === token.id ? 'Revoke now?' : 'Revoke'}
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                    {rows.length === 0 && !tokens.isLoading && (
                        <tr>
                            <td colSpan={6} className="ptable-empty">
                                No read token yet: one lets a service, a script or Claude Code read the logs
                                {props.admin ? '' : ' (an admin creates it)'}.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ClickHouse watching itself: the volume's room, the memory against its cap, and what each environment keeps.
function StoragePanel({ now }: { now: number }): ReactNode {
    const storage = useQuery({ queryKey: ['storage'], queryFn: api.storage, refetchInterval: 60_000 });
    const data = storage.data;
    if (!data) {
        return null;
    }
    const used = data.disk.totalBytes - data.disk.freeBytes;
    const diskShare = data.disk.totalBytes > 0 ? used / data.disk.totalBytes : 0;
    const memoryShare = data.memory.capBytes > 0 ? data.memory.usedBytes / data.memory.capBytes : 0;
    const tone = (share: number): string => (share >= 0.9 ? 'bad' : share >= 0.8 ? 'warn' : '');
    const day = 24 * 3600_000;

    return (
        <div className="panel">
            <div className="panel-head">
                <span className="title">Storage</span>
                <span className="note">
                    ClickHouse {data.version} · up {ago(now - data.uptimeSeconds * 1000, now)}
                </span>
            </div>
            <div className="gauges">
                <div className={`gauge ${tone(diskShare)}`}>
                    <span className="micro">Volume</span>
                    <span className="gbar">
                        <b style={{ width: `${Math.max(1, diskShare * 100)}%` }} />
                    </span>
                    <span className="mono small">
                        {formatBytes(used)} of {formatBytes(data.disk.totalBytes)} · {(diskShare * 100).toFixed(1)}%
                    </span>
                </div>
                <div className={`gauge ${tone(memoryShare)}`}>
                    <span className="micro">Memory</span>
                    <span className="gbar">
                        <b style={{ width: `${Math.max(1, memoryShare * 100)}%` }} />
                    </span>
                    <span className="mono small">
                        {formatBytes(data.memory.usedBytes)} of its {formatBytes(data.memory.capBytes)} cap · {(memoryShare * 100).toFixed(0)}%
                    </span>
                </div>
            </div>
            <table className="ptable compact">
                <thead>
                    <tr className="micro">
                        <th>Environment</th>
                        <th className="num">Lines</th>
                        <th className="num">On disk</th>
                        <th className="num">Raw</th>
                        <th className="num">Compressed</th>
                        <th>From</th>
                        <th>To</th>
                        <th className="num">Days</th>
                    </tr>
                </thead>
                <tbody>
                    {data.envs.map((env) => (
                        <tr key={env.env}>
                            <td>
                                <EnvBadge env={env.env} />
                            </td>
                            <td className="num mono" title={formatCount(env.rows)}>
                                {compactCount(env.rows)}
                            </td>
                            <td className="num mono">{formatBytes(env.diskBytes)}</td>
                            <td className="num mono">{formatBytes(env.rawBytes)}</td>
                            <td className="num mono">{env.diskBytes > 0 ? `${(env.rawBytes / env.diskBytes).toFixed(1)}×` : '–'}</td>
                            <td className="mono small">{shortStamp(env.firstAt, 'rome')}</td>
                            <td className="mono small">{shortStamp(env.lastAt, 'rome')}</td>
                            <td className="num mono">{Math.max(1, Math.ceil((env.lastAt - env.firstAt) / day))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
